import type { TranslationKey } from './es'

// English dictionary — must declare every TranslationKey.
const en: Record<TranslationKey, string> = {
  // ── Common ─────────────────────────────────────────────
  'common.refresh': 'Refresh',
  'common.new': 'New',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.close': 'Close',
  'common.loading': 'Loading…',
  'common.all': 'All',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.active': 'Active',
  'common.inactive': 'Inactive',
  'common.discontinued': 'Discontinued',
  'common.status': 'Status',
  'common.actions': 'Actions',
  'common.filters': 'Filters',
  'common.category': 'Category',
  'common.brand': 'Brand',
  'common.name': 'Name',
  'common.price': 'Price',
  'common.stock': 'Stock',
  'common.supplier': 'Supplier',
  'common.product': 'Product',
  'common.products': 'Products',
  'common.date': 'Date',
  'common.client': 'Customer',
  'common.total': 'Total',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.pageOf': 'Page {page} of {pages} • Total: {total}',
  'common.allCategories': 'All categories',
  'common.allBrands': 'All brands',
  'common.allStatuses': 'All statuses',
  'common.user': 'User',
  'common.role': 'Role',
  'common.email': 'Email',
  'common.optional': '(optional)',
  'common.viewDetails': 'View details',
  'common.updateStatus': 'Update status',
  'common.error': 'Error',
  'common.remove': 'Remove',

  // ── Roles ──────────────────────────────────────────────
  'role.superadmin': 'Superadmin',
  'role.admin': 'Admin',
  'role.client': 'Customer',
  'role.viewer': 'Viewer',
  'role.superadminLong': 'Super Administrator',
  'role.adminLong': 'Administrator',

  // ── Admin layout ───────────────────────────────────────
  'nav.digestor': 'Catalog Processor',
  'nav.inventory': 'Inventory',
  'nav.suppliers': 'Suppliers',
  'nav.products': 'Products',
  'nav.quotations': 'Orders & Quotes',
  'nav.sales': 'Sales',
  'nav.users': 'Users',
  'nav.settings': 'Settings',
  'layout.online': 'Online',
  'layout.logout': 'Log out',
  'layout.adminPanel': 'Admin Panel',
  'layout.language': 'Language',

  // ── Order statuses ─────────────────────────────────────
  'status.pending': 'Pending',
  'status.confirmed': 'Confirmed',
  'status.processing': 'Processing',
  'status.shipped': 'Shipped',
  'status.delivered': 'Delivered',
  'status.cancelled': 'Cancelled',
  'status.refunded': 'Refunded',

  // ── Quotation statuses ─────────────────────────────────
  'qstatus.draft': 'Draft',
  'qstatus.sent': 'Sent',
  'qstatus.viewed': 'Viewed',
  'qstatus.approved': 'Approved',
  'qstatus.rejected': 'Rejected',
  'qstatus.expired': 'Expired',
  'qstatus.converted': 'Converted',

  // ── Product availability ───────────────────────────────
  'avail.in_stock': 'In stock',
  'avail.low_stock': 'Low stock',
  'avail.out_of_stock': 'Out of stock',
  'avail.discontinued': 'Discontinued',
  'avail.pre_order': 'Pre-order',

  // ── Inventory ──────────────────────────────────────────
  'inv.title': 'Inventory Management',
  'inv.subtitle': 'Manage products, stock and suppliers',
  'inv.export': 'Export',
  'inv.import': 'Import',
  'inv.receipt': 'Receiving',
  'inv.history': 'History',
  'inv.newProduct': 'New Product',
  'inv.alertsTitle': 'Stock alerts:',
  'inv.alertsCount': '{count} products with low stock or reorder point reached',
  'inv.viewAlerts': 'View alerts',
  'inv.alertsModal': 'Stock Alerts',
  'inv.colAvailable': 'Available',
  'inv.colMin': 'Min',
  'inv.colReorder': 'Reorder',
  'inv.historyModal': 'Adjustment History',
  'inv.colType': 'Type',
  'inv.colPrev': 'Previous',
  'inv.colNew': 'New',
  'inv.colReason': 'Reason',
  'inv.receiptModal': 'Product Receiving',
  'inv.reference': 'Reference',
  'inv.refPlaceholder': 'PO/Invoice/Waybill',
  'inv.optionalMark': '(Optional)',
  'inv.colProductId': 'Product ID',
  'inv.colQuantity': 'Quantity',
  'inv.addRow': 'Add row',
  'inv.superTools': 'Superadmin Tools',
  'inv.superOnly': 'Only visible to superadmin',
  'inv.wipeAll': 'Delete inventory and products',
  'inv.wipeMetrics': 'Reset global metrics',
  'inv.confirmWipe': 'Are you sure you want to delete ALL inventory and ALL products? (includes stock, adjustments, media and references). This action cannot be undone.',
  'inv.confirmResetMetrics': 'Reset general dashboard metrics to zero?',
  'inv.wiped': 'Inventory deleted',
  'inv.wipeError': 'Could not delete inventory',
  'inv.metricsReset': 'Metrics reset',
  'inv.metricsResetError': 'Could not reset metrics',
  'inv.searchPh': 'Search products by name or SKU...',
  'inv.itemsPerPage': 'Items per page',
  'inv.showing': 'Showing {from} - {to} of {total} products',
  'inv.viewDetails': 'View details',
  'inv.adjustStock': 'Adjust stock',
  'inv.moreOptions': 'More options',
  'inv.stockOut': 'Out of Stock',
  'inv.stockLow': 'Low Stock',
  'inv.stockIn': 'In Stock',
  'inv.adjustTitle': 'Adjust stock — {name}',
  'inv.adjType': 'Type',
  'inv.adjIn': 'Stock in',
  'inv.adjOut': 'Stock out',
  'inv.adjSet': 'Set',
  'inv.adjNewStock': 'New stock',
  'inv.adjQuantity': 'Quantity',
  'inv.adjReasonPh': 'Receiving, count adjustment, shrinkage, etc.',
  'inv.stockAdjusted': 'Stock adjusted',
  'inv.adjustError': 'Could not adjust stock',
  'inv.deleteTitle': 'Delete product',
  'inv.deleteConfirm': 'Are you sure you want to delete this product? This action cannot be undone.',
  'inv.editTitle': 'Edit product',
  'inv.reorderPoint': 'Reorder point',
  'inv.loadError': 'Could not load products',
  'inv.updated': 'Product updated',
  'inv.updateError': 'Could not update',
  'inv.deleted': 'Product deleted',
  'inv.deleteError': 'Could not delete',
  'inv.receiptSaved': 'Receiving recorded',
  'inv.receiptError': 'Could not record receiving',
  'inv.addValidRows': 'Add valid rows',
  'inv.exportOk': 'Exported {count} products',
  'inv.exportError': 'Could not export',
  'inv.uploadOk': '✅ Inventory updated: {count} products processed successfully',
  'inv.uploadError': 'Error processing file',
  'inv.testOk': '✅ Test completed: {count} files processed',
  'inv.testError': 'Processing test failed',
  'inv.csvHeader': 'Name;SKU;Category;Brand;Price;Stock;Supplier;Status',

  // ── Products ───────────────────────────────────────────
  'prod.title': 'Products',
  'prod.new': 'New Product',
  'prod.searchPh': 'Search by name, SKU or brand',
  'prod.editTitle': 'Edit product',
  'prod.newTitle': 'New product',
  'prod.supplierRequired': 'Supplier *',
  'prod.select': 'Select…',
  'prod.supplierCode': 'Supplier code',
  'prod.supplierSkuPh': 'Supplier SKU',
  'prod.nameRequired': 'Name *',
  'prod.internalSku': 'Internal SKU',
  'prod.categoryRequired': 'Category *',
  'prod.availability': 'Availability',
  'prod.none': 'No products found',
  'prod.deleteTitle': 'Delete',
  'prod.deleteConfirm': 'Are you sure you want to delete this product?',
  'prod.loadError': 'Could not load products',
  'prod.updated': 'Product updated',
  'prod.created': 'Product created',
  'prod.selectSupplier': 'Select a supplier',
  'prod.nameCatRequired': 'Name and category are required',
  'prod.saveError': 'Could not save',
  'prod.deleted': 'Product deleted',
  'prod.deleteError': 'Could not delete',
  'prod.tabMine': 'My products',
  'prod.tabCatalog': 'Supplier catalog',
  'prod.catalogNote': 'Offer imported from supplier catalogs by the Digestor — not your own stock.',
  'prod.allSuppliers': 'All suppliers',
  'prod.confidence': 'Confidence',
  'prod.catalogEmpty': 'No catalog items — import one from the Digestor',
  'prod.catalogSearchPh': 'Search catalog (name, SKU, spec)',

  // ── Suppliers ──────────────────────────────────────────
  'sup.title': 'Suppliers',
  'sup.searchPh': 'Search by name or code',
  'sup.colCode': 'Code',
  'sup.colLastSync': 'Last sync',
  'sup.none': 'No suppliers found',
  'sup.editTitle': 'Edit supplier',
  'sup.newTitle': 'New supplier',
  'sup.activeField': 'Active',
  'sup.loadError': 'Could not load suppliers',
  'sup.nameCodeRequired': 'Name and code are required',
  'sup.saved': 'Supplier saved',
  'sup.saveError': 'Could not save',
  'sup.deleted': 'Supplier deleted',
  'sup.deleteError': 'Could not delete',

  // ── Orders & Quotations ────────────────────────────────
  'quo.title': 'Orders & Quotes',
  'quo.ordersTab': 'Orders ({count})',
  'quo.quotesTab': 'Quotes ({count})',
  'quo.searchOrderPh': 'Search by number or customer',
  'quo.searchQuotePh': 'Search by customer or ID',
  'quo.colOrderNum': 'Order #',
  'quo.colValidUntil': 'Valid until',
  'quo.colCreated': 'Created',
  'quo.noOrders': 'No orders',
  'quo.noQuotes': 'No quotes recorded',
  'quo.orderModalTitle': 'Update status — {num}',
  'quo.quoteModalTitle': 'Update quote',
  'quo.ordersLoadError': 'Could not load orders',
  'quo.quotesLoadError': 'Could not load quotes',
  'quo.statusUpdated': 'Status updated',
  'quo.quoteStatusUpdated': 'Quote status updated',
  'quo.updateError': 'Could not update',

  // ── Web catalog quotes (catalog_quote_requests) ──
  'cq.tab': 'Web quotes',
  'cq.tabCount': 'Web quotes ({count})',
  'cq.notifTitle': 'Notifications on receipt',
  'cq.notifAdminLabel': 'Team notice',
  'cq.notifClientLabel': 'Client confirmation',
  'cq.notifAdminOk': '✓ team via {via}',
  'cq.notifClientOk': '✓ client via {via}',
  'cq.notifAdminNone': 'team not notified',
  'cq.notifAdminNoneTitle': 'No channel configured or delivery failed — see details',
  'cq.notifNone': 'not sent',
  'cqstatus.nueva': 'New',
  'cqstatus.enRevision': 'Under review',
  'cqstatus.cotizada': 'Quoted',
  'cqstatus.enviada': 'Sent',
  'cqstatus.cerrada': 'Closed',
  'cq.searchPh': 'Search by number, client, email or WhatsApp',
  'cq.colNumber': 'Quote #',
  'cq.colContact': 'Contact',
  'cq.colItems': 'Items',
  'cq.colEstTotal': 'Est. total',
  'cq.open': 'Open',
  'cq.empty': 'No catalog quote requests',
  'cq.loadError': 'Could not load web quotes',
  'cq.updateError': 'Could not update the quote',
  'cq.saved': 'Prices saved',
  'cq.company': 'Company',
  'cq.clientNote': 'Client note',
  'cq.globalMargin': 'Global margin %',
  'cq.applyMargin': 'Apply prices',
  'cq.aiCalc': 'Price with AI (CortexBlood)',
  'cq.aiRunning': 'Asking AI…',
  'cq.aiDone': 'AI suggestions ready — review and apply',
  'cq.aiApply': 'Apply AI suggestions',
  'cq.aiApplied': 'Suggestions applied to prices',
  'cq.aiFailed': 'AI did not respond ({reason}) — manual pricing available',
  'cq.total': 'Total',
  'cq.colProduct': 'Product',
  'cq.colQty': 'Qty',
  'cq.colCost': 'Cat. cost',
  'cq.colMargin': 'Margin %',
  'cq.colSalePrice': 'Sale price',
  'cq.colSubtotal': 'Subtotal',
  'cq.noBaseCost': 'No base cost — manual or AI-suggested price',
  'cq.sendWhatsApp': 'Send via WhatsApp',
  'cq.sendEmail': 'Send via Email',
  'cq.waTooltip': 'Opens WhatsApp with the quote ready for the client',
  'cq.emailTooltipMailto': 'Opens your mail client with the prefilled quote (SMTP not configured)',
  'cq.emailTooltipSmtp': 'Sends the quote through the SMTP server',
  'cq.sentWhatsApp': 'Registered as sent via WhatsApp',
  'cq.sentEmail': 'Quote sent by email',
  'cq.sentEmailClient': 'Registered — quote sent from your mailbox',
  'cq.emailFailed': 'Could not send the email',
  'cq.sentVia': 'sent via {via} {at}',
  'cq.pfTitle': 'Pre-invoice (Proforma)',
  'cq.pfPending': 'No proforma yet — generate it to download and send to the client',
  'cq.pfGenerate': 'Generate proforma PDF',
  'cq.pfGenerating': 'Generating PDF…',
  'cq.pfGenerated': 'Proforma {number} ready',
  'cq.pfError': 'Could not process the proforma',
  'cq.pfDownload': 'Download PDF',
  'cq.pfPublicLink': 'Public PDF link',
  'cq.pfRefresh': 'Refresh preview',
  'cq.qtyHint': 'Wholesale quantity: ±1/±10/±100/±1000 buttons or direct field',

  // ── Sales ──────────────────────────────────────────────
  'sales.title': 'Sales',
  'sales.subtitle': 'Manage all platform orders and sales',
  'sales.totalOrders': 'Total Orders',
  'sales.pending': 'Pending',
  'sales.totalRevenue': 'Total Revenue',
  'sales.thisMonth': 'This Month',
  'sales.searchPh': 'Search by order number or customer...',
  'sales.loadingOrders': 'Loading orders...',
  'sales.noOrdersFiltered': 'No orders found with the applied filters',
  'sales.noOrders': 'No orders recorded',
  'sales.colOrder': 'Order',
  'sales.colProducts': 'Products',
  'sales.itemCount': '{count} product(s)',
  'sales.pageInfo': 'Page {page} of {pages} • Total: {total} orders',
  'sales.modalTitle': 'Update Order Status',
  'sales.orderLabel': 'Order:',
  'sales.clientLabel': 'Customer:',
  'sales.newStatus': 'New Status',
  'sales.tracking': 'Tracking Number (optional)',
  'sales.trackingPh': 'E.g.: 1234567890',
  'sales.carrier': 'Carrier (optional)',
  'sales.carrierPh': 'E.g.: DHL, FedEx, UPS',
  'sales.adminNotes': 'Admin Notes (optional)',
  'sales.notesPh': 'Internal notes about the order...',
  'sales.updating': 'Updating...',
  'sales.updateBtn': 'Update Status',
  'sales.loadError': 'Error loading sales data',
  'sales.statusUpdated': 'Order status updated',
  'sales.updateError': 'Error updating order status',

  // ── Users ──────────────────────────────────────────────
  'users.title': 'Users',
  'users.subtitle': 'System user management',
  'users.new': 'New User',
  'users.searchPh': 'Search by name or email',
  'users.colLastLogin': 'Last login',
  'users.colCreated': 'Created',
  'users.activeFilter': 'Active',
  'users.inactiveFilter': 'Inactive',
  'users.never': 'Never',
  'users.none': 'No users found',
  'users.deactivate': 'Deactivate',
  'users.reactivate': 'Reactivate',
  'users.editTitle': 'Edit user',
  'users.newTitle': 'New user',
  'users.newPassword': 'New password (optional)',
  'users.passwordRequired': 'Password *',
  'users.firstName': 'First name *',
  'users.lastName': 'Last name *',
  'users.loadError': 'Could not load users',
  'users.updated': 'User updated',
  'users.created': 'User created',
  'users.required': 'Email, password, first and last name are required',
  'users.saveError': 'Could not save',
  'users.cantDeactivateSelf': 'You cannot deactivate yourself',
  'users.deactivated': 'User deactivated',
  'users.reactivated': 'User reactivated',
  'users.statusError': 'Could not change status',

  // ── Settings ───────────────────────────────────────────
  'set.title': 'Settings',
  'set.subtitle': 'System and account status',
  'set.systemStatus': 'System status',
  'set.environment': 'Environment',
  'set.database': 'Database',
  'set.verified': 'Verified',
  'set.connected': 'Connected',
  'set.noData': 'No data',
  'set.dbRecords': 'Database records',
  'set.yourAccount': 'Your account',
  'set.count.users': 'Users',
  'set.count.suppliers': 'Suppliers',
  'set.count.products': 'Products',
  'set.count.orders': 'Orders',
  'set.count.quotations': 'Quotes',
  'set.count.inventory_items': 'Inventory items',
  'set.count.catalog_items': 'Unified catalog items',
  'set.loadError': 'Could not load system info',
  'set.uptime': 'Uptime',

  // ── Login ──────────────────────────────────────────────
  'auth.brandTitle': 'Smart restaurant management',
  'auth.brandDesc': 'Inventory, quotes and orders in a single platform. Built for chefs and restaurants across Latin America.',
  'auth.f1Title': 'Unified inventory',
  'auth.f1Desc': 'Real-time stock control',
  'auth.f2Title': 'Quotes in minutes',
  'auth.f2Desc': 'Multi-format Excel with high accuracy',
  'auth.f3Title': 'Live analytics',
  'auth.f3Desc': 'Data-driven decisions',
  'auth.welcome': 'Welcome back',
  'auth.welcomeSub': 'Sign in to manage inventory and quotes',
  'auth.password': 'Password',
  'auth.remember': 'Remember me',
  'auth.forgot': 'Forgot your password?',
  'auth.signingIn': 'Signing in...',
  'auth.signIn': 'Sign in',
  'auth.or': 'or',
  'auth.createAccount': 'Create new account',
  'auth.loginError': 'Sign in failed',

  // ── Register ───────────────────────────────────────────
  'reg.title': 'Create account',
  'reg.subtitle': 'Sign up to start managing your inventory',
  'reg.firstName': 'First name',
  'reg.lastName': 'Last name',
  'reg.confirm': 'Confirm password',
  'reg.firstNameReq': 'First name is required',
  'reg.lastNameReq': 'Last name is required',
  'reg.emailInvalid': 'Invalid email',
  'reg.pwdMin': 'Minimum 6 characters',
  'reg.pwdMismatch': 'Passwords do not match',
  'reg.pwdHint': 'Use uppercase letters, numbers and symbols for better security.',
  'reg.creating': 'Creating account...',
  'reg.create': 'Create account',
  'reg.hasAccount': 'Already have an account?',
  'reg.signIn': 'Sign in',
  'reg.created': 'Account created successfully',

  // ── Forgot password ────────────────────────────────────
  'fp.title': 'Reset password',
  'fp.desc': 'Enter your email to receive a recovery link.',
  'fp.send': 'Send link',
  'fp.alert': 'A recovery link will be sent to: {email} (demo)',

  // ── AuthLayout ─────────────────────────────────────────
  'authLayout.b1': 'Unified inventory and quotes in minutes',
  'authLayout.b2': 'Process multi-format Excel with high accuracy',
  'authLayout.b3': 'Optimized media synced to your catalog',

  // ── UploadWizard ───────────────────────────────────────
  'wiz.title': 'Import Inventory (SAP-style)',
  'wiz.supplierOptional': 'Supplier (optional)',
  'wiz.autoDetect': 'Auto-detect',
  'wiz.autoDetectHint': 'The system will try to detect the format automatically if you do not select a supplier.',
  'wiz.dropActive': 'Drop the files here…',
  'wiz.dropIdle': 'Drag and drop Excel files here or click to select',
  'wiz.formats': 'Formats: .xlsx, .xls • Max 10MB per file',
  'wiz.colFile': 'File',
  'wiz.colSize': 'Size',
  'wiz.colStatus': 'Status',
  'wiz.colProgress': 'Progress',
  'wiz.emptyQueue': 'No files in the queue',
  'wiz.queued': 'Queued',
  'wiz.uploading': 'Uploading',
  'wiz.processed': 'Processed',
  'wiz.failed': 'Error',
  'wiz.summary': 'Queued: {q} • OK: {p} • Errors: {f}',
  'wiz.process': 'Process',
  'wiz.pause': 'Pause',
  'wiz.clearQueue': 'Clear Queue',
  'wiz.results': 'Results',
  'wiz.colSupplier': 'Supplier',
  'wiz.colProducts': 'Products',
  'wiz.colTime': 'Time',
  'wiz.uploadError': 'Error uploading file',

  // ── Catalog digestor (CortexCatalogProcessorPage) ──
  'digest.title': 'Bulk Catalog Digester',
  'digest.formatsBadge': 'Excel · CSV · TXT · PDF · Word · Images + Local AI',
  'digest.subtitle': 'Upload batches of price lists in any format/language. The deterministic engine + CortexBlood AI normalizes, deduplicates and unifies them into the global catalog.',
  'digest.aiBadgeSession': 'No API response (session)',
  'digest.aiBadgeLatency': 'latency {ms}ms',
  'digest.aiBadgeOffline': 'CortexBlood not responding — deterministic engine only',
  'digest.dropReady': '{n} file(s) ready to digest',
  'digest.dropHint': 'Drag catalog batches or click',
  'digest.dropFormats': '.xlsx .xls .csv .txt .pdf .docx .png .jpg — any encoding or language',
  'digest.digestBtn': 'Digest {n} file(s)',
  'digest.digesting': 'Digesting batch…',
  'digest.addFileErr': 'Add at least one catalog file',
  'digest.tooLarge': 'File(s) rejected for size (max {max}MB): {names}',
  'digest.jobQueued': 'Batch queued: {n} files — you can close this tab, processing continues on the server',
  'digest.jobDone': 'Batch finished: {ok}/{total} files OK, {n} products inserted',
  'digest.tabCloseNote': 'Processing runs on the server: if you close this tab you can follow the status in the jobs list.',
  'digest.uploadMB': '{loaded} / {total} MB',
  'digest.uploading': 'Uploading {pct}%',
  'digest.stage.queued': 'Queued',
  'digest.stage.reading': 'Reading document',
  'digest.stage.extracting': 'Extracting products',
  'digest.stage.enriching': 'Enriching with AI',
  'digest.stage.ocr': 'Vision OCR',
  'digest.stage.images': 'Extracting photos',
  'digest.stage.persisting': 'Saving to catalog',
  'digest.stage.pages': 'page {done}/{total}',
  'digest.stage.ai': 'AI {done}/{total}',
  'digest.stage.inserted': '{n} saved',
  'digest.stage.products': '{n} products',
  'digest.stage.imagesMatched': '{n} photos',
  'digest.jobCompleted': 'Completed',
  'digest.jobFailed': 'Failed',
  'digest.jobProcessing': 'Processing',
  'digest.jobSummary': '{ok}/{total} OK · {failed} failed · {inserted} inserted · {dupes} duplicates',
  'digest.jobProducts': '{n} prod',
  'digest.hybridTag': 'Hybrid Deterministic + AI Engine',
  'digest.hybridTitle': 'Tolerant of real supplier chaos',
  'digest.hybridBody': 'No fixed column order. Detects headers in any row, ES/EN/ZH synonyms (品名, precio, SKU, 单价…), UTF-8/Latin-1/GBK encodings, promotional rows, and if confidence is low it calls the local LLM (Ollama). Every batch goes through CortexBlood: translated and canonicalized names, unified categories, and then a semantic scan detects duplicates the hash cannot catch.',
  'digest.featBatch': 'Batch',
  'digest.featBatchBody': '100+ files per batch, isolated failure per file',
  'digest.featDedup': 'AI Dedupe',
  'digest.featDedupBody': 'Exact hash + LLM semantic confirmation',
  'digest.featAI': 'Always-on AI',
  'digest.featAIBody': 'CortexBlood enriches and translates every product',
  'digest.dedupTitle': 'AI-detected duplicates',
  'digest.dedupSub': 'Second CortexBlood pass over the catalog — runs automatically after each batch · same product, different name/supplier',
  'digest.dedupScanning': 'analyzing {items} items · {ok}/{total} groups…',
  'digest.dedupBtn': 'Scan with CortexBlood',
  'digest.dedupEmptyDone': 'Scan completed — no confirmed semantic duplicates',
  'digest.dedupEmpty': 'No pending groups. Launch the AI scan to detect semantic duplicates.',
  'digest.dedupMerge': 'Merge',
  'digest.dedupDismiss': 'Not dups',
  'digest.dedupCanonical': 'canonical',
  'digest.dedupDupPct': '{pct}% dup',
  'digest.dedupScanToast': 'AI scan: {ok} duplicate groups out of {total} candidates',
  'digest.dedupScanFail': 'AI scan failed: {err}',
  'digest.dedupScanStart': 'CortexBlood scanning semantic duplicates…',
  'digest.dedupLaunchFail': 'Could not launch the scan',
  'digest.dedupMerged': 'Merged: {n} items absorbed',
  'digest.dedupDismissed': 'Group dismissed',
  'digest.dedupResolveFail': 'Could not resolve',
  'digest.netError': 'Network error: {err}',
  'digest.catalogTitle': 'Unified Catalog',
  'digest.catalogStats': '{items} products · {suppliers} suppliers',
  'digest.catalogAiItems': '{n} normalized by AI',
  'digest.searchPlaceholder': 'Search name / SKU / spec…',
  'digest.allSuppliers': 'All suppliers',
  'digest.allCategories': 'All categories',
  'digest.filterBtn': 'Filter',
  'digest.colProduct': 'Product',
  'digest.colSku': 'SKU',
  'digest.colSupplier': 'Supplier',
  'digest.colCategory': 'Category',
  'digest.colSpec': 'Spec / Cap / Power',
  'digest.colPrice': 'Price USD',
  'digest.colStock': 'Stock',
  'digest.colConf': 'Conf.',
  'digest.colSource': 'Source',
  'digest.catalogEmpty': 'No products. Digest a batch of catalogs above.',
  'digest.pagination': '{total} products · page {page} of {pages}',
  'digest.prevPage': '← Previous',
  'digest.nextPage': 'Next →',
  'digest.aiExtractTitle': 'Full extraction by LLM',
  'digest.aiEnhanceTitle': 'Products normalized/enriched by CortexBlood',
  // Specific errors (backend errorCode → i18n)
  'digest.err.pdfScanned': 'Scanned PDF with no text layer — requires vision OCR',
  'digest.err.pdfPassword': 'Password-protected PDF — remove the password and upload again',
  'digest.err.pdfCorrupt': 'Corrupt or truncated PDF',
  'digest.err.pdfUnreadable': 'The PDF could not be read',
  'digest.err.unsupported': 'File type not supported for digestion',
  'digest.err.badSpreadsheet': 'Could not be read as a spreadsheet',
  'digest.err.badDocx': 'Could not read the Word document',
  'digest.err.fileUnreadable': 'The file could not be read from disk',
  'digest.err.interrupted': 'Interrupted by a service restart — upload it again',
  'digest.err.empty': 'No readable table could be extracted from the file',
  'digest.err.noProducts': 'No products found in the file',
  'digest.err.unknown': 'Unknown extraction error',
  'digest.err.tooLargeHttp': 'File exceeds the server limit ({max}MB per file)',
  'digest.err.timeout': 'Upload took too long — slow connection or very large file',
  'digest.err.network': 'No connection to the server — check your network',
  'digest.err.batch': 'Failed to queue batch (HTTP {status})',
  // ── Prospects ──
  'nav.prospects': 'Prospects',
  'nav.cotizaia': 'cotizaIA',
  'pros.title': 'Potential Clients',
  'pros.aiOnline': 'online',
  'pros.aiOffline': 'offline',
  'pros.hideSearch': 'Hide search',
  'pros.newSearch': 'New search',
  'pros.discoverTitle': 'Discover prospects across the Americas',
  'pros.stepCountry': '1. Country',
  'pros.stepRegion': '2. {label}',
  'pros.selectRegion': '— Select {label} —',
  'pros.stepCities': '3. Cities',
  'pros.citiesIn': '({n} in {province})',
  'pros.pickRegionFirst': '↑ Select a {label} first',
  'pros.stepSegments': '4. Segments',
  'pros.stepKeywords': '5. Keywords (optional, comma-separated)',
  'pros.keywordsPh': 'seafood, pizza, sushi…',
  'pros.stepLimit': '6. Limit: {n} prospects',
  'pros.allCountries': 'All countries',
  'pros.colCountry': 'Country',
  'pros.searching': 'Searching…',
  'pros.searchIn': 'Search in {n} {cities}',
  'pros.cityOne': 'city',
  'pros.cityMany': 'cities',
  'pros.sourcesNote': 'Free sources: OpenStreetMap/Overpass + web search · scoring with CortexBlood',
  'pros.starting': 'Starting…',
  'pros.progress': 'Cities {done}/{total} · {found} prospects found',
  'pros.resultSummary': '{inserted} new · {found} found · {duplicates} already existed · {scored} AI-scored',
  'pros.aiUnavailable': ' · AI unavailable (no score)',
  'pros.sourcesFailed': 'Sources without response: {list}',
  'pros.searchFailed': 'Search failed',
  'pros.totalCount': '{n} prospects',
  'pros.plural': 'prospects',
  'pros.withScore': 'with fit_score',
  'pros.avgScore': ' (avg {avg})',
  'pros.searchPh': 'Search by name, address or cuisine',
  'pros.allSegments': 'All segments',
  'pros.scoreAll': 'Score: all',
  'pros.colBusiness': 'Business',
  'pros.colSegment': 'Segment',
  'pros.colCity': 'City',
  'pros.colContact': 'Contact',
  'pros.colScore': 'Fit score',
  'pros.empty': 'No prospects — launch a search above',
  'pros.noScore': 'No AI score',
  'pros.aiReasonNotes': 'AI reason & notes',
  'pros.aiAnalysis': 'AI analysis',
  'pros.noAnalysis': 'No analysis — AI was unavailable at discovery',
  'pros.source': 'Source: {src}',
  'pros.notes': 'Notes',
  'pros.notesPh': 'Follow-up, contact, next steps…',
  'pros.saveNote': 'Save note',
  'pros.noteSaved': 'Note saved',
  'pros.pagination': '{total} prospects · page {page} of {pages}',
  'pros.loadError': 'Could not load prospects',
  'pros.cityRequired': 'Select at least one city',
  'pros.segmentRequired': 'Select at least one segment',
  'pros.searchStarted': 'Search started — this may take a few minutes',
  'pros.searchStartError': 'Could not start the search',
  'pros.updateError': 'Could not update',
  'pros.jobDone': '{inserted} new prospects ({found} found)',
  'pros.jobDoneScored': ', {scored} AI-scored',
  'pros.statusChanged': 'Status → {status}',
  'pros.approach': 'Approach',
  'pstatus.new': 'New',
  'pstatus.contacted': 'Contacted',
  'pstatus.qualified': 'Qualified',
  'pstatus.discarded': 'Discarded',

  // ── Admin catalog (CatalogPage) ──
  'nav.catalog': 'Catalog',
  'cat.title': 'Catalog',
  'cat.subtitle': 'Everything digested from supplier catalogs — filter by every attribute',
  'cat.searchPh': 'Search by name, SKU or spec…',
  'cat.moreFilters': 'More filters',
  'cat.brandPh': 'Brand (in name or spec)',
  'cat.capacity': 'Capacity',
  'cat.capacityPh': 'E.g.: 16L, 350ml',
  'cat.power': 'Power',
  'cat.powerPh': 'E.g.: 410W, 60,000 BTU',
  'cat.dimensions': 'Dimensions',
  'cat.dimensionsPh': 'E.g.: 120 x 90',
  'cat.unit': 'Unit',
  'cat.allUnits': 'All units',
  'cat.priceMin': 'Min price (USD)',
  'cat.priceMax': 'Max price (USD)',
  'cat.confAny': 'Any confidence',
  'cat.confMid': 'Medium or high (≥50%)',
  'cat.confHigh': 'High only (≥80%)',
  'cat.hasImage': 'With photo',
  'cat.inStock': 'In stock',
  'cat.clear': 'Clear filters',
  'cat.loadError': 'Could not load catalog',
  'cat.empty': 'No results with these filters — adjust your search',
  'cat.pagination': '{total} products · page {page} of {pages}',
  'cat.noPhoto': 'No photo',
  'cat.askPrice': 'Ask',


  // ── Dispatch logistics (provider discovery) ──
  'logi.aiAnalysis': 'AI analysis',
  'logi.aiOffline': 'offline',
  'logi.aiOnline': 'online',
  'logi.aiReasonNotes': 'AI reason and notes',
  'logi.aiUnavailable': 'AI unavailable — results without scoring',
  'logi.allCountries': 'All countries',
  'logi.allTypes': 'All types',
  'logi.approach': 'Approach',
  'logi.avgScore': 'avg score {avg}',
  'logi.channelActive': 'Mark channel active',
  'logi.citiesIn': '({n} in {province})',
  'logi.cityMany': 'cities',
  'logi.cityOne': 'city',
  'logi.cityRequired': 'Select at least one city',
  'logi.colCity': 'City',
  'logi.colContact': 'Contact',
  'logi.colCountry': 'Country',
  'logi.colProvider': 'Provider',
  'logi.colScore': 'Score',
  'logi.colType': 'Type',
  'logi.discoverTitle': 'Discover delivery providers',
  'logi.empty': 'No providers — start a discovery above',
  'logi.hideSearch': 'Hide search',
  'logi.jobDone': '{inserted} new of {found} found',
  'logi.jobDoneScored': ' · {scored} AI-scored',
  'logi.keywordsPh': 'e.g. delivery, motorbike, courier',
  'logi.loadError': 'Could not load providers',
  'logi.newSearch': 'New search',
  'logi.noAnalysis': 'No AI analysis',
  'logi.noScore': 'No score',
  'logi.notes': 'Notes',
  'logi.noteSaved': 'Note saved',
  'logi.notesPh': 'Internal note…',
  'logi.pagination': '{total} providers · page {page} of {pages}',
  'logi.pickRegionFirst': 'Pick a {label} first',
  'logi.plural': 'providers',
  'logi.progress': '{done}/{total} cities · {found} found',
  'logi.resultSummary': '{inserted} inserted · {found} found · {duplicates} duplicates · {scored} scored',
  'logi.saveNote': 'Save',
  'logi.scoreAll': 'All scores',
  'logi.searchFailed': 'Search failed',
  'logi.searchIn': 'Search {n} {cities}',
  'logi.searching': 'Searching…',
  'logi.searchPh': 'Search provider…',
  'logi.searchStarted': 'Discovery started',
  'logi.searchStartError': 'Could not start discovery',
  'logi.selectRegion': 'Select {label}',
  'logi.source': 'Source: {src}',
  'logi.sourcesFailed': 'Failed sources: {list}',
  'logi.sourcesNote': 'Sources: public directories, maps and local web (public data only)',
  'logi.starting': 'Starting…',
  'logi.statusChanged': 'Status → {status}',
  'logi.stepCities': 'Cities',
  'logi.stepCountry': 'Country',
  'logi.stepKeywords': 'Keywords (optional)',
  'logi.stepLimit': 'Limit per city: {n}',
  'logi.stepRegion': '{label}',
  'logi.stepTypes': 'Provider types',
  'logi.title': 'Dispatch logistics',
  'logi.typeRequired': 'Select at least one provider type',
  'logi.updateError': 'Could not update',
  'logi.withScore': 'with score',
  'lstatus.new': 'New',
  'lstatus.contacted': 'Contacted',
  'lstatus.negotiating': 'Negotiating',
  'lstatus.active_channel': 'Active channel',
  'lstatus.discarded': 'Discarded',

  // ── Upload wizard (inventory import) ──

}

export default en
